import { useState, type FormEvent } from 'react';
import { Card, CardBody, CardFooter, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PasswordInput } from '@/components/common/PasswordInput';
import { CopyableField } from '@/components/common/CopyableField';
import { StatusChip } from '@/components/common/StatusChip';
import { useClipboard } from '@/hooks/useClipboard';
import { notify } from '@/lib/notify';
import { apiErrorMessage } from '@/api/errors';
import { QrCode } from '@/features/mfa/QrCode';
import type { EnrollmentStart } from '@/features/mfa/api';
import {
  useConfirmEnrollment, useDisableMfa, useMfaStatus, useRegenerateCodes, useStartEnrollment, useStepUp,
} from '@/features/mfa/hooks';
import { ArrowDownTrayIcon, ClipboardDocumentIcon, ShieldCheckIcon } from '@/icons';

const SIX = /^\d{6}$/;
const fmtDate = (iso: string | null) => (iso ? new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium' }).format(new Date(iso)) : '—');
/** JBSWY3DPEHPK3PXP -> JBSW Y3DP EHPK 3PXP (easier to type into an app). */
const groupKey = (k: string) => k.replace(/(.{4})/g, '$1 ').trim();

function CodeInput({ id, label, value, onChange, error, recovery = false }: {
  id: string; label: string; value: string; onChange: (v: string) => void; error?: string; recovery?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-fg">{label}</label>
      <input id={id} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={recovery ? 'off' : 'one-time-code'}
        inputMode={recovery ? 'text' : 'numeric'} spellCheck={false} placeholder={recovery ? 'abcde-fghjk' : '123456'}
        aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined}
        className="h-11 w-full rounded-lg border border-border bg-surface px-3 font-mono text-base tracking-widest text-fg focus:border-brand focus:ring-2 focus:ring-brand/30" />
      {error && <p id={`${id}-err`} className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}

/** Recovery codes, shown exactly once. Closing is only offered after the user confirms they saved them. */
export function RecoveryCodesPanel({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const { copied, copy } = useClipboard();
  const text = `Nifty Hosting recovery codes (each works once)\n\n${codes.join('\n')}\n`;
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'nifty-hosting-recovery-codes.txt'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-fg">
        Save these recovery codes somewhere safe, like a password manager. Each one signs you in once if you lose
        your phone. <strong>They will not be shown again.</strong>
      </p>
      <ol aria-label="Recovery codes" className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-surface-raised/40 p-3 font-mono text-sm text-fg">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="flat" startContent={<ClipboardDocumentIcon className="h-4 w-4" />} onPress={() => void copy(text)}>
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button size="sm" variant="flat" startContent={<ArrowDownTrayIcon className="h-4 w-4" />} onPress={download}>Download .txt</Button>
      </div>
      <label className="flex items-center gap-2 text-sm text-fg">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="h-4 w-4" />
        I have saved my recovery codes
      </label>
      <Button color="primary" isDisabled={!saved} onPress={onDone}>Done</Button>
    </div>
  );
}

type EnableStep = { step: 'password' } | { step: 'scan'; enrollment: EnrollmentStart } | { step: 'codes'; codes: string[] };

function EnableDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [state, setState] = useState<EnableStep>({ step: 'password' });
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const start = useStartEnrollment();
  const confirm = useConfirmEnrollment();
  const close = () => { setState({ step: 'password' }); setPassword(''); setCode(''); setError(undefined); onClose(); };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    try {
      const enrollment = await start.mutateAsync(password);
      setPassword('');
      setState({ step: 'scan', enrollment });
    } catch (err) { setError(apiErrorMessage(err, 'Could not start setup.')); }
  };
  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    const c = code.replace(/\s+/g, '');
    if (!SIX.test(c)) { setError('Enter the 6-digit code shown in your authenticator app.'); return; }
    setError(undefined);
    try {
      const { recoveryCodes } = await confirm.mutateAsync(c);
      setCode('');
      setState({ step: 'codes', codes: recoveryCodes }); // the secret is dropped here
    } catch (err) { setError(apiErrorMessage(err, 'That code is not valid. Check the time on your phone and try again.')); }
  };

  return (
    <Modal isOpen={isOpen} onClose={state.step === 'codes' ? () => undefined : close} hideClose={state.step === 'codes'}
      title="Set up two-step verification" size="lg">
      {state.step === 'password' && (
        <form onSubmit={submitPassword} className="space-y-4 pb-2" noValidate>
          <p className="text-sm text-fg-muted">Confirm it’s you. You’ll need an authenticator app such as Google Authenticator, Microsoft Authenticator, 1Password or Authy.</p>
          <PasswordInput label="Current password" autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} isInvalid={!!error} errorMessage={error} />
          <Button color="primary" type="submit" isLoading={start.isPending} isDisabled={!password}>Continue</Button>
        </form>
      )}
      {state.step === 'scan' && (
        <form onSubmit={submitCode} className="space-y-4 pb-2" noValidate>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-fg">
            <li>Open your authenticator app and add an account.</li>
            <li>Scan this QR code, or type the setup key below.</li>
            <li>Enter the 6-digit code the app shows.</li>
          </ol>
          <div className="flex justify-center rounded-lg bg-white p-3">
            <QrCode value={state.enrollment.otpauthUri} label="QR code to add Nifty Hosting to your authenticator app" />
          </div>
          <CopyableField label="Setup key (if you can’t scan)" value={groupKey(state.enrollment.secret)} />
          <CodeInput id="mfa-enroll-code" label="6-digit code" value={code} onChange={setCode} error={error} />
          <p className="text-xs text-fg-muted">This setup expires in 10 minutes. Nothing is enabled until the code is confirmed.</p>
          <div className="flex gap-2">
            <Button color="primary" type="submit" isLoading={confirm.isPending}>Turn on</Button>
            <Button variant="flat" onPress={close}>Cancel</Button>
          </div>
        </form>
      )}
      {state.step === 'codes' && (
        <div className="pb-2">
          <p role="status" className="mb-3 flex items-center gap-2 text-sm font-medium text-success">
            <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" /> Two-step verification is on. Other sessions were signed out.
          </p>
          <RecoveryCodesPanel codes={state.codes} onDone={() => { notify.success('Two-step verification is on'); close(); }} />
        </div>
      )}
    </Modal>
  );
}

/** Password + code dialog used for turning off and for replacing recovery codes. */
function ConfirmDialog({ mode, isOpen, onClose }: { mode: 'disable' | 'regenerate'; isOpen: boolean; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string>();
  const [codes, setCodes] = useState<string[] | null>(null);
  const disable = useDisableMfa();
  const regenerate = useRegenerateCodes();
  const close = () => { setPassword(''); setCode(''); setRecovery(false); setError(undefined); setCodes(null); onClose(); };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    const c = code.trim();
    if (!recovery && !SIX.test(c.replace(/\s+/g, ''))) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    try {
      if (mode === 'disable') {
        await disable.mutateAsync({ currentPassword: password, ...(recovery ? { recoveryCode: c } : { code: c.replace(/\s+/g, '') }) });
        notify.success('Two-step verification is off');
        close();
      } else {
        const res = await regenerate.mutateAsync({ currentPassword: password, code: c.replace(/\s+/g, '') });
        setCodes(res.recoveryCodes);
      }
    } catch (err) { setError(apiErrorMessage(err, 'Check your password and code and try again.')); }
  };
  const title = mode === 'disable' ? 'Turn off two-step verification' : 'Replace recovery codes';
  return (
    <Modal isOpen={isOpen} onClose={codes ? () => undefined : close} hideClose={!!codes} title={title}>
      {codes ? (
        <div className="pb-2"><RecoveryCodesPanel codes={codes} onDone={() => { notify.success('New recovery codes saved; old ones no longer work'); close(); }} /></div>
      ) : (
        <form onSubmit={submit} className="space-y-4 pb-2" noValidate>
          <p className="text-sm text-fg-muted">
            {mode === 'disable'
              ? 'Your account will be protected by your password only. Other sessions will be signed out.'
              : 'Your current recovery codes will stop working.'}
          </p>
          <PasswordInput label="Current password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <CodeInput id={`mfa-${mode}-code`} label={recovery ? 'Recovery code' : '6-digit code'} value={code} onChange={setCode} error={error} recovery={recovery} />
          {mode === 'disable' && (
            <button type="button" className="text-sm text-brand hover:underline" onClick={() => { setRecovery((v) => !v); setCode(''); }}>
              {recovery ? 'Use my authenticator app instead' : 'Use a recovery code instead'}
            </button>
          )}
          <div className="flex gap-2">
            <Button color={mode === 'disable' ? 'danger' : 'primary'} type="submit" isDisabled={!password || !code}
              isLoading={disable.isPending || regenerate.isPending}>{mode === 'disable' ? 'Turn off' : 'Replace codes'}</Button>
            <Button variant="flat" onPress={close}>Cancel</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** Re-confirm the second factor for sensitive actions (role changes, billing administration). */
function StepUpForm() {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const stepUp = useStepUp();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const c = code.replace(/\s+/g, '');
    if (!SIX.test(c)) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setError(undefined);
    try {
      const r = await stepUp.mutateAsync(c);
      setCode('');
      notify.success(`Confirmed for ${Math.round(r.verifiedFor / 60)} minutes`);
    } catch (err) { setError(apiErrorMessage(err, 'That code is not valid. Try the next one.')); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2" noValidate aria-label="Confirm it’s you for sensitive actions">
      <div className="w-48"><CodeInput id="mfa-stepup-code" label="Code for sensitive actions" value={code} onChange={setCode} error={error} /></div>
      <Button type="submit" variant="flat" isLoading={stepUp.isPending}>Confirm it’s me</Button>
    </form>
  );
}

/** Settings → Security: two-step verification with an authenticator app (TOTP). */
export function TwoStepCard() {
  const status = useMfaStatus();
  const [dialog, setDialog] = useState<'enable' | 'disable' | 'regenerate' | null>(null);
  const enabled = !!status.data?.totp.enabled;
  return (
    <Card className="max-w-2xl">
      <CardHeader title="Two-step verification"
        description="Sign in with your password and a code from an authenticator app. Required for sensitive actions such as changing roles or billing administration."
        actions={status.data && <StatusChip tone={enabled ? 'success' : 'neutral'}>{enabled ? 'On' : 'Off'}</StatusChip>} />
      <CardBody>
        {status.isLoading && <p role="status" className="text-sm text-fg-muted">Loading…</p>}
        {status.isError && <p role="alert" className="text-sm text-danger">{apiErrorMessage(status.error, 'Could not load two-step verification status.')}</p>}
        {status.data && !status.data.available && (
          <p role="alert" className="text-sm text-warning">Two-step verification is temporarily unavailable. Try again later.</p>
        )}
        {status.data && enabled && (
          <div className="space-y-4">
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div><dt className="text-xs text-fg-muted">Method</dt><dd className="text-fg">Authenticator app</dd></div>
              <div><dt className="text-xs text-fg-muted">Turned on</dt><dd className="text-fg">{fmtDate(status.data.totp.confirmedAt)}</dd></div>
              <div><dt className="text-xs text-fg-muted">Recovery codes left</dt>
                <dd className={status.data.recoveryCodesRemaining <= 3 ? 'text-warning' : 'text-fg'}>{status.data.recoveryCodesRemaining} of 10</dd></div>
            </dl>
            <StepUpForm />
          </div>
        )}
        {status.data && !enabled && status.data.available && (
          <p className="text-sm text-fg-muted">Two-step verification is off. Turn it on to protect your account if your password is ever stolen.</p>
        )}
      </CardBody>
      <CardFooter>
        {status.data && !enabled && status.data.available && (
          <Button color="primary" startContent={<ShieldCheckIcon className="h-4 w-4" />} onPress={() => setDialog('enable')}>Turn on</Button>
        )}
        {status.data && enabled && (
          <div className="flex flex-wrap gap-2">
            <Button variant="flat" onPress={() => setDialog('regenerate')}>Replace recovery codes</Button>
            <Button variant="flat" color="danger" onPress={() => setDialog('disable')}>Turn off</Button>
          </div>
        )}
      </CardFooter>
      <EnableDialog isOpen={dialog === 'enable'} onClose={() => setDialog(null)} />
      <ConfirmDialog mode="disable" isOpen={dialog === 'disable'} onClose={() => setDialog(null)} />
      <ConfirmDialog mode="regenerate" isOpen={dialog === 'regenerate'} onClose={() => setDialog(null)} />
    </Card>
  );
}
